import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { TrendingUp, Plus, Trash2, Edit, Loader2, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

export default function UpsellFunnels() {
  const [funnels, setFunnels] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '', trigger_product_ids: [],
    upsell_product_id: '', upsell_price: '', upsell_headline: 'Tunggu! Ada Penawaran Spesial Untuk Anda!',
    upsell_description: '', upsell_image_url: '',
    downsell_product_id: '', downsell_price: '', downsell_headline: 'Bagaimana Dengan Penawaran Ini?',
    downsell_description: '', downsell_image_url: '',
    is_active: true, display_order: 0
  });

  const fetchData = async () => {
    try {
      const [funnelRes, prodRes] = await Promise.all([
        api.get('/tenant/upsell-funnels'),
        api.get('/tenant/products?limit=200')
      ]);
      setFunnels(funnelRes.data.upsell_funnels || []);
      setProducts(prodRes.data.products || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setForm({
      name: '', trigger_product_ids: [],
      upsell_product_id: '', upsell_price: '', upsell_headline: 'Tunggu! Ada Penawaran Spesial Untuk Anda!',
      upsell_description: '', upsell_image_url: '',
      downsell_product_id: '', downsell_price: '', downsell_headline: 'Bagaimana Dengan Penawaran Ini?',
      downsell_description: '', downsell_image_url: '',
      is_active: true, display_order: 0
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.upsell_product_id) {
      toast.error('Nama dan produk upsell wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        upsell_price: form.upsell_price ? parseInt(form.upsell_price) : null,
        downsell_price: form.downsell_price ? parseInt(form.downsell_price) : null,
        downsell_product_id: form.downsell_product_id || null,
      };
      if (editingId) {
        await api.put(`/tenant/upsell-funnels/${editingId}`, payload);
        toast.success('Upsell funnel berhasil diupdate');
      } else {
        await api.post('/tenant/upsell-funnels', payload);
        toast.success('Upsell funnel berhasil dibuat');
      }
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleEdit = (f) => {
    setForm({
      name: f.name,
      trigger_product_ids: f.trigger_product_ids || [],
      upsell_product_id: f.upsell_product_id,
      upsell_price: f.upsell_price || '',
      upsell_headline: f.upsell_headline || '',
      upsell_description: f.upsell_description || '',
      upsell_image_url: f.upsell_image_url || '',
      downsell_product_id: f.downsell_product_id || '',
      downsell_price: f.downsell_price || '',
      downsell_headline: f.downsell_headline || '',
      downsell_description: f.downsell_description || '',
      downsell_image_url: f.downsell_image_url || '',
      is_active: f.is_active,
      display_order: f.display_order || 0
    });
    setEditingId(f.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus upsell funnel ini?')) return;
    try {
      await api.delete(`/tenant/upsell-funnels/${id}`);
      toast.success('Upsell funnel dihapus');
      fetchData();
    } catch (e) { toast.error('Gagal menghapus'); }
  };

  const handleTriggerToggle = (productId) => {
    setForm(prev => {
      const ids = prev.trigger_product_ids.includes(productId)
        ? prev.trigger_product_ids.filter(id => id !== productId)
        : [...prev.trigger_product_ids, productId];
      return { ...prev, trigger_product_ids: ids };
    });
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin h-8 w-8 text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="upsell-funnels-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Upsell & Downsell</h1>
          <p className="text-sm text-slate-500">Tambahkan penawaran pasca-pembelian untuk meningkatkan nilai order</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="create-funnel-btn">
          <Plus size={16} className="mr-2" /> Buat Funnel
        </Button>
      </div>

      {showForm && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">{editingId ? 'Edit Upsell Funnel' : 'Buat Upsell Funnel Baru'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nama Funnel *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama funnel" data-testid="funnel-name-input" />
                </div>
                <div>
                  <Label>Urutan</Label>
                  <Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} />
                </div>
              </div>

              {/* Trigger Products */}
              <div>
                <Label>Produk Pemicu (kosong = semua produk)</Label>
                <p className="text-xs text-slate-400 mb-2">Pilih produk yang memicu tampilnya upsell setelah checkout</p>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto border rounded-md p-2">
                  {products.map(p => (
                    <Badge
                      key={p.id}
                      variant={form.trigger_product_ids.includes(p.id) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => handleTriggerToggle(p.id)}
                    >
                      {p.name}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Upsell Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                  <TrendingUp size={16} className="text-green-600" /> Penawaran Upsell *
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Produk Upsell *</Label>
                    <select className="w-full border border-slate-200 rounded-md p-2 text-sm" value={form.upsell_product_id} onChange={(e) => setForm({ ...form, upsell_product_id: e.target.value })} data-testid="upsell-product-select">
                      <option value="">Pilih produk...</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>{p.name} - Rp {fmt(p.price)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Harga Spesial (kosong = harga asli)</Label>
                    <Input type="number" value={form.upsell_price} onChange={(e) => setForm({ ...form, upsell_price: e.target.value })} placeholder="Harga spesial upsell" />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Headline Upsell</Label>
                  <Input value={form.upsell_headline} onChange={(e) => setForm({ ...form, upsell_headline: e.target.value })} placeholder="Tunggu! Ada Penawaran Spesial!" data-testid="upsell-headline-input" />
                </div>
                <div className="mt-3">
                  <Label>Deskripsi Upsell</Label>
                  <Textarea value={form.upsell_description} onChange={(e) => setForm({ ...form, upsell_description: e.target.value })} placeholder="Jelaskan mengapa customer perlu produk ini..." rows={2} />
                </div>
                <div className="mt-3">
                  <Label>URL Gambar Upsell</Label>
                  <Input value={form.upsell_image_url} onChange={(e) => setForm({ ...form, upsell_image_url: e.target.value })} placeholder="https://..." />
                </div>
              </div>

              {/* Downsell Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                  <ArrowDown size={16} className="text-orange-500" /> Penawaran Downsell (Opsional)
                </h3>
                <p className="text-xs text-slate-400 mb-3">Ditampilkan jika customer menolak upsell</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Produk Downsell</Label>
                    <select className="w-full border border-slate-200 rounded-md p-2 text-sm" value={form.downsell_product_id} onChange={(e) => setForm({ ...form, downsell_product_id: e.target.value })} data-testid="downsell-product-select">
                      <option value="">Tidak ada downsell</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>{p.name} - Rp {fmt(p.price)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Harga Spesial Downsell</Label>
                    <Input type="number" value={form.downsell_price} onChange={(e) => setForm({ ...form, downsell_price: e.target.value })} placeholder="Harga spesial downsell" />
                  </div>
                </div>
                {form.downsell_product_id && (
                  <>
                    <div className="mt-3">
                      <Label>Headline Downsell</Label>
                      <Input value={form.downsell_headline} onChange={(e) => setForm({ ...form, downsell_headline: e.target.value })} placeholder="Bagaimana Dengan Penawaran Ini?" />
                    </div>
                    <div className="mt-3">
                      <Label>Deskripsi Downsell</Label>
                      <Textarea value={form.downsell_description} onChange={(e) => setForm({ ...form, downsell_description: e.target.value })} placeholder="Alternatif yang lebih terjangkau..." rows={2} />
                    </div>
                    <div className="mt-3">
                      <Label>URL Gambar Downsell</Label>
                      <Input value={form.downsell_image_url} onChange={(e) => setForm({ ...form, downsell_image_url: e.target.value })} placeholder="https://..." />
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Aktif</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} data-testid="save-funnel-btn">
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                  {editingId ? 'Update' : 'Simpan'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Batal</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {funnels.length === 0 ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="py-12 text-center">
              <TrendingUp size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Belum ada upsell funnel</p>
              <p className="text-xs text-slate-400 mt-1">Buat funnel untuk menampilkan penawaran setelah checkout</p>
            </CardContent>
          </Card>
        ) : (
          funnels.map(f => (
            <Card key={f.id} className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                      <TrendingUp size={20} className="text-green-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{f.name}</span>
                        <Badge variant={f.is_active ? 'default' : 'secondary'}>
                          {f.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">
                        Upsell: {f.upsell_product?.name || f.upsell_product_id}
                        {f.upsell_price && ` · Rp ${fmt(f.upsell_price)}`}
                      </p>
                      {f.downsell_product_id && (
                        <p className="text-xs text-slate-400">
                          Downsell: {f.downsell_product?.name || f.downsell_product_id}
                          {f.downsell_price && ` · Rp ${fmt(f.downsell_price)}`}
                        </p>
                      )}
                      {f.trigger_product_ids?.length > 0 && (
                        <p className="text-xs text-slate-400">
                          Pemicu: {f.trigger_product_ids.length} produk
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(f)}>
                      <Edit size={14} className="mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(f.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
